'use client';

import { KeyboardEvent, MouseEventHandler } from 'react';
import { ChevronDown, XIcon } from 'lucide-react';

import { useTranslations } from '@northladder/i18n';

import { cn } from '../../lib';

import { Button } from '../button';
import { PopoverTrigger } from '../popover';
import { Separator } from '../separator';

import { OtherSelectedItemsBadge } from './OtherSelectedItemsBadge';
import { SelectedItemBadge } from './SelectedItemBadge';
import type { ITriggerBtnProps } from './types';

/**
 * -----------------------------------------------------------------------------
 * Trigger button for the multi-select component.
 */
export function TriggerBtn({
  animDelay,
  animDuration,
  autoSize,
  buttonRef,
  classBadgeAnimation,
  className,
  isDisabled,
  isMobileScreen,
  isPopoverOpen,
  isSingleLine,
  listboxId,
  onClearExtraOptions,
  onClearFilters,
  onGetOptionByValue,
  onToggleOption,
  onTogglePopover,
  placeholder,
  placeholderClassName,
  responsiveSettings,
  selectedCountId,
  selectedItemsCount,
  selectedValues,
  total,
  triggerDescriptionId,
  variant,
  widthConstraints,
  ...props
}: ITriggerBtnProps) {
  const t = useTranslations('DesignSystemComponents.MultiSelect');

  const { maxCount: maxItemCount, compactMode: isCompactMode } =
    responsiveSettings;
  const { maxWidth } = widthConstraints;

  const selectedItems = selectedValues
    .slice(0, maxItemCount)
    .map((value) => {
      const option = onGetOptionByValue(value);

      return option ? { option, value } : null;
    })
    .filter((item) => !!item);

  const handleOnClearFilters: MouseEventHandler<HTMLDivElement> = (event) => {
    event.stopPropagation();
    onClearFilters();
  };

  const handleOnPressEnter = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      onClearFilters();
    }
  };

  return (
    <PopoverTrigger asChild>
      <Button
        ref={buttonRef}
        {...props}
        aria-controls={isPopoverOpen ? listboxId : undefined}
        aria-describedby={`${triggerDescriptionId} ${selectedCountId}`}
        aria-expanded={isPopoverOpen}
        aria-haspopup="listbox"
        aria-label={t('aria.ctaLabel', {
          count: selectedItemsCount,
          total,
          placeholder,
        })}
        className={cn(
          `min-h-9 flex h-auto items-center justify-between rounded-md border
        bg-inherit p-1 py-0 hover:bg-inherit [&_svg]:pointer-events-auto`,
          autoSize ? 'w-auto' : 'w-full',
          isCompactMode && 'min-h-8 text-sm',
          isMobileScreen && 'min-h-10 text-base',
          isDisabled && 'cursor-not-allowed opacity-50',
          className
        )}
        disabled={isDisabled}
        onClick={onTogglePopover}
        role="combobox"
        style={{
          ...widthConstraints,
          maxWidth: `min(${maxWidth}, 100%)`,
        }}
      >
        {selectedItemsCount > 0 ? (
          <div className="flex w-full items-center justify-between">
            <div
              className={cn(
                'flex items-center gap-1',
                isSingleLine
                  ? 'multiselect-singleline-scroll overflow-x-auto'
                  : 'flex-wrap',
                isCompactMode && 'gap-0.5'
              )}
              style={isSingleLine ? { paddingBottom: '4px' } : {}}
            >
              {selectedItems.map(({ option, value }) => (
                <SelectedItemBadge
                  key={value}
                  animDelay={animDelay}
                  animDuration={animDuration}
                  classBadgeAnimation={classBadgeAnimation}
                  isCompactMode={isCompactMode}
                  isIconEnabled={!responsiveSettings.hideIcons}
                  isMobileScreen={isMobileScreen}
                  isSingleLine={isSingleLine}
                  onToggleOption={onToggleOption}
                  option={option}
                  value={value}
                  variant={variant}
                />
              ))}

              {selectedItemsCount > maxItemCount ? (
                <OtherSelectedItemsBadge
                  animDelay={animDelay}
                  animDuration={animDuration}
                  classBadgeAnimation={classBadgeAnimation}
                  isCompactMode={isCompactMode}
                  isSingleLine={isSingleLine}
                  moreCount={selectedItemsCount - maxItemCount}
                  onClearExtraOptions={onClearExtraOptions}
                  variant={variant}
                />
              ) : null}
            </div>

            <div className="flex items-center justify-between">
              <div
                aria-label={t('aria.clearLabel', { count: selectedItemsCount })}
                className="
                text-muted-foreground hover:text-foreground focus:ring-ring
                mx-2 flex h-4 w-4 cursor-pointer items-center justify-center
                rounded-sm focus:outline-none focus:ring-2 focus:ring-offset-1
              "
                onClick={handleOnClearFilters}
                onKeyDown={handleOnPressEnter}
                role="button"
                tabIndex={0}
              >
                <XIcon className="h-4 w-4" />
              </div>
              <Separator
                className="min-h-6 flex h-full"
                orientation="vertical"
              />
              <ChevronDown
                aria-hidden="true"
                className="text-muted-foreground mx-2 h-4 cursor-pointer"
              />
            </div>
          </div>
        ) : (
          <div className="mx-auto flex w-full items-center justify-between">
            <span
              className={cn(
                'mx-3 text-sm',
                placeholderClassName || 'text-muted-foreground'
              )}
            >
              {placeholder}
            </span>
            <ChevronDown className="text-muted-foreground mx-2 h-4 cursor-pointer" />
          </div>
        )}
      </Button>
    </PopoverTrigger>
  );
}
